import { useMediaQuery } from '@mui/material';
import { intervalToDuration } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { ORISResponse, useAsyncAction, useTransaction } from '../../../../../hooks';
import { Icon, Icons, Tooltip, Typography } from '../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../../../stories/atoms/containerV2/ContainerV2';
import { Charts } from '../../../../../stories/molecules/charts/Charts';
import { muiTheme } from '../../../../../theme';
import { homeTranslations } from '../../../../../translations';
import { Translation, isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { StyledChartIcon, StyledChartTypography, StyledIcon } from './OrisCustomersWidget.styles';

type TransactionSectionProps = {
  icon: Icons;
  isMobileView: boolean;
  testId: string;
  value: number;
  text: Translation;
  tooltipText: Translation;
  valueFormatter?: (value: number) => string;
};

const TransactionSection: React.FC<TransactionSectionProps> = ({
  icon,
  isMobileView,
  testId,
  value,
  text,
  tooltipText,
  valueFormatter,
}) => {
  const { t } = useTranslation();

  return (
    <RowFlexContainer
      borderRadius={[2]}
      hasBorder
      padding={[1, 3]}
      justifyContent="between"
      alignItems="center"
      flex={1}
      data-testid={testId}
    >
      <RowFlexContainer data-testid="hello" alignItems="center">
        <Icon icon={icon} margin={[0, 2, 0, 0]} size="small" />
        <Typography>{t(text.key, text.defaultValue)}</Typography>
        <Tooltip
          arrowedToolTip
          text={
            <ContainerV2 padding={[1, 2]}>
              <Typography color="white">{t(tooltipText.key, tooltipText.defaultValue)}</Typography>
            </ContainerV2>
          }
        >
          <StyledIcon data-testid="info-icon" icon={Icons.INFO} size="small" margin={[0, 1]} />
        </Tooltip>
      </RowFlexContainer>
      <Typography margin={isMobileView ? [1, 0, 0] : [0]} type="body3">
        {valueFormatter ? valueFormatter(value) : value}
      </Typography>
    </RowFlexContainer>
  );
};

type OrisCustomersWidgetProps = {
  currentDate: Date;
  interval: StorePerformanceInterval;
};

export const OrisCustomersWidget: FC<OrisCustomersWidgetProps> = observer(({ currentDate, interval }) => {
  const { t } = useTranslation();
  const { orisCustomers: orisCustomersTranslations } = homeTranslations;
  const { fetchORISData } = useTransaction();

  const {
    result: orisData,
    isLoading: orisLoading,
    isError: orisError,
    triggerAction: triggerFetchOrisAction,
  } = useAsyncAction<ORISResponse>();

  const fetchData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchORISData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [interval, currentDate]);

  useEffect(() => {
    triggerFetchOrisAction(fetchData);
  }, [currentDate]);

  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));

  let oristConversionRate =
    (orisData?.totalORISCustomer ?? 0) === 0
      ? 0
      : ((orisData?.convertedORISCustomers ?? 0) / (orisData?.totalORISCustomer ?? 0)) * 100;
  oristConversionRate = Number.parseFloat(oristConversionRate.toFixed(2));

  const getSaleTimeText = (seconds: number) => {
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 });

    const secondsLabel = t(
      orisCustomersTranslations.duration.seconds.key,
      orisCustomersTranslations.duration.seconds.defaultValue,
    );

    const minutesLabel = t(
      orisCustomersTranslations.duration.minutes.key,
      orisCustomersTranslations.duration.minutes.defaultValue,
    );

    const hoursLabel = t(
      orisCustomersTranslations.duration.hours.key,
      orisCustomersTranslations.duration.hours.defaultValue,
    );

    const secondsText = duration.seconds ? `${duration.seconds} ${secondsLabel}` : `0 ${secondsLabel}`;

    const minutesText = duration.minutes ? `${duration.minutes} ${minutesLabel} ` : '';

    const hoursText = duration.hours ? `${duration.hours} ${hoursLabel} ` : '';

    return `${hoursText}${minutesText}${secondsText}`;
  };

  const getAverageSaleTime = (orisTotalSaleTime?: number, totalOrisTransactions?: number) => {
    if (!orisTotalSaleTime || orisTotalSaleTime === 0) return 0;

    if (!totalOrisTransactions || totalOrisTransactions === 0) return 0;

    return orisTotalSaleTime / totalOrisTransactions;
  };

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(orisCustomersTranslations.title.key, orisCustomersTranslations.title.defaultValue)}
      subHeaderText={t(orisCustomersTranslations.subTitle.key, orisCustomersTranslations.subTitle.defaultValue)}
      isLoading={orisLoading}
      isError={orisError}
      errorText={t(orisCustomersTranslations.error.key, orisCustomersTranslations.error.defaultValue)}
      toolTipText={t(orisCustomersTranslations.toolTip.key, orisCustomersTranslations.toolTip.defaultValue)}
      isNoData={(orisData?.totalORISCustomer ?? 0) === 0}
      lastUpdatedDate={orisData?.lastUpdated}
      content={
        <ColumnFlexContainer data-testid="oris-widget-wrapper" padding={[2, 4, 4]} height="100%">
          <RowFlexContainer alignItems="center" justifyContent="between" data-testid="oris-chart-container">
            <RowFlexContainer>
              <Icon data-testid="conversion" size="small" icon={Icons.CONVERSION} margin={[0, 2, 0]} />
              <Typography type="button">
                {t(
                  orisCustomersTranslations.orisConversionRate.text.key,
                  orisCustomersTranslations.orisConversionRate.text.defaultValue,
                )}
              </Typography>
            </RowFlexContainer>
            <ContainerV2
              position="relative"
              width="100px"
              height={isMobileView ? '70px' : '85px'}
              data-testid="oris-chart"
            >
              <Charts
                chartType="donut"
                chartSeries={[oristConversionRate, 100 - oristConversionRate]}
                xAxisCategories={['', '']}
                areaChartGradient={{
                  type: ['solid'],
                }}
                chartColors={['validationGreen', 'backgroundSelectedHover']}
                chartPlotOptions={{
                  customScale: isMobileView ? 1.5 : 1.2,
                  offsetY: 15,
                  donut: {
                    size: '80%',
                    labels: {
                      show: false,
                    },
                  },
                }}
                legend={{
                  show: false,
                }}
                strokeWidth={[0]}
              />
              <StyledChartTypography type={isMobileView ? 'body2' : 'button'} color="validationGreen">
                {oristConversionRate}%
              </StyledChartTypography>
              <Tooltip
                arrowedToolTip
                text={
                  <ContainerV2 padding={[1, 2]}>
                    <Typography color="white">
                      {t(
                        orisCustomersTranslations.orisConversionRate.tooltip.key,
                        orisCustomersTranslations.orisConversionRate.tooltip.defaultValue,
                      )}
                    </Typography>
                  </ContainerV2>
                }
              >
                <StyledChartIcon data-testid="info-icon" icon={Icons.INFO} size="small" margin={[0, 1]} />
              </Tooltip>
            </ContainerV2>
          </RowFlexContainer>

          <ColumnFlexContainer flex={1} gap={[2]}>
            <TransactionSection
              icon={Icons.PROFILE}
              isMobileView={isMobileView}
              testId="total-transactions-section"
              value={orisData?.totalORISCustomer ?? 0}
              text={orisCustomersTranslations.orisTotalTransactions.text}
              tooltipText={orisCustomersTranslations.orisTotalTransactions.tooltip}
            />
            <TransactionSection
              icon={Icons.LOYALTYCUSTOMER}
              isMobileView={isMobileView}
              testId="total-loyalty-transactions-section"
              value={orisData?.loyalOrisCustomerCount ?? 0}
              text={orisCustomersTranslations.orisTotalLoyaltyTransactions.text}
              tooltipText={orisCustomersTranslations.orisTotalLoyaltyTransactions.tooltip}
            />
            <TransactionSection
              icon={Icons.NEWMEMBER}
              isMobileView={isMobileView}
              testId="total-converted-transactions-section"
              value={orisData?.convertedORISCustomers ?? 0}
              text={orisCustomersTranslations.orisConvertedTransactions.text}
              tooltipText={orisCustomersTranslations.orisConvertedTransactions.tooltip}
            />
            <TransactionSection
              icon={Icons.PROFILETIME}
              isMobileView={isMobileView}
              testId="average-oris-time-section"
              value={getAverageSaleTime(orisData?.orisTotalSaleTime, orisData?.totalORISCustomer)}
              valueFormatter={(value) => getSaleTimeText(value)}
              text={orisCustomersTranslations.averageOrisTime.text}
              tooltipText={orisCustomersTranslations.averageOrisTime.tooltip}
            />
          </ColumnFlexContainer>
        </ColumnFlexContainer>
      }
    />
  );
});
